import React, { useEffect, useRef } from 'react';
import { ToggleSensitiveDataButtonProps, SENSITIVE_VISIBLE_TIMEOUT } from 'framework/sensitiveData';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, CircularProgress, IconButton } from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ToggleSensitiveDataIconButton = ({
  isTimerActive = false,
  isShown = false,
  ...other
}: ToggleSensitiveDataButtonProps) => {
  const { key, ...props } = other;

  const [progress, setProgress] = React.useState(100);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (isShown) {
      setProgress(100);
    }

    if (isTimerActive) {
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        const elapsedTime = Date.now() - (startTimeRef.current || 0);
        const remainingPercentage = Math.max(0, 100 - (elapsedTime / SENSITIVE_VISIBLE_TIMEOUT) * 100);

        setProgress(remainingPercentage);

        if (remainingPercentage <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }, 100);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isShown, isTimerActive]);

  return (
    <>
      {isShown ? (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress variant="determinate" value={progress} sx={{ color: 'border.default', width: 2 }} />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconButton {...props} size="small" color="primary" sx={{ p: 0 }}>
              <VisibilityIcon
                sx={{
                  p: 0,
                  color: 'primary.main',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <IconButton {...props} size="small" color="secondary">
          <VisibilityOff sx={{ color: 'primary.main' }} />
        </IconButton>
      )}
    </>
  );
};

export default ToggleSensitiveDataIconButton;
